"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getBrowserClient } from "@/utils/supabase/client";
import { invalidatePublicNavState, seedPublicNavState } from "@/lib/public-auth";
import { PostgrestError, Subscription, User } from '@supabase/supabase-js';
import * as Sentry from "@sentry/nextjs";
import { phAlias, phCapture, phIdentify, phRegister, phRegisterOnce, phReset, phStartSessionRecording } from "@/utils/analytics";
import { getAttributionForEvent } from "@/utils/attribution";
import { SubscriptionData } from "../types/subscriptions";
import { healGmailEmail } from "@/lib/actions/email-healing";
import { normalizeEmailForLookup } from "@/lib/email-utils";
import { hasSubscriptionHistory } from "@/lib/billing/status";
import { isLocalBypassAuthEnabled } from "@/lib/local-bypass-auth";
import { revokeThisDevice } from "@/lib/push/revoke-device";
import { categorizeOtpError } from "@/lib/auth/otp-errors";
import type { DiamondPass } from "@/lib/billing/diamond-pass";

const TRIAL_DURATION_DAYS = 5;

function isExpectedMissingSession(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AuthSessionMissingError"
  );
}

function registerTrialMetadata(userId: string, subscriptions: SubscriptionData[]): void {
  const trialSub = subscriptions.find((s) => s.status === "trialing");
  if (!trialSub) return;

  const trialStartMs = new Date(trialSub.period_start).getTime();
  const trialDay = Math.min(
    Math.max(Math.floor((Date.now() - trialStartMs) / (1000 * 60 * 60 * 24)), 0),
    TRIAL_DURATION_DAYS - 1,
  );

  phRegister({
    is_trial_user: true,
    trial_start_date: trialSub.period_start,
    trial_day: trialDay,
    subscription_status: "trialing",
  });

  phRegisterOnce({ first_trial_session: true });

  phIdentify(userId, {
    is_trial_user: true,
    trial_start_date: trialSub.period_start,
    subscription_status: "trialing",
  });

  phStartSessionRecording();
}

export interface UserProfileData {
  first_name: string;
  last_name: string;
  address: string;
  city?: string;
  state?: string;
  postal_code?: string;
  rewardful_via?: string;
}

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  subscriptions: SubscriptionData[];
  diamondPass: DiamondPass | null;
  isAuthenticated: boolean;
  hasVerifiedEmail: boolean;
  hasSubscriptionHistory: boolean;
  hasOddsScreenAccess: boolean;
  sendOTPCode: (email: string) => Promise<{ error: any | null }>;
  verifyOTPAndSignIn: (email: string, otp: string) => Promise<{ error: any | null }>;
  resendOTPCode: (email: string) => Promise<{ error: any | null }>;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signUp: (email: string, password: string, userData: UserProfileData, stripeCustomerId?: string) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  updateProfile: (userData: UserProfileData) => Promise<{ userData: UserProfileData | null, error: any | null }>;
  getProfileData: () => Promise<{ userData: UserProfileData | null, error: any | null }>;
}

const unauthenticatedAuthContextValue: AuthContextValue = {
  user: null,
  isLoading: false,
  subscriptions: [],
  diamondPass: null,
  isAuthenticated: false,
  hasVerifiedEmail: false,
  hasSubscriptionHistory: false,
  hasOddsScreenAccess: false,
  sendOTPCode: async () => ({ error: new Error("AuthProvider not found") }),
  verifyOTPAndSignIn: async () => ({ error: new Error("AuthProvider not found") }),
  resendOTPCode: async () => ({ error: new Error("AuthProvider not found") }),
  signIn: async () => ({ error: new Error("AuthProvider not found") }),
  signUp: async () => ({ error: new Error("AuthProvider not found") }),
  signOut: async () => { },
  refreshSubscription: async () => { },
  updateProfile: async () => ({ userData: null, error: new Error("AuthProvider not found") }),
  getProfileData: async () => ({ userData: null, error: new Error("AuthProvider not found") })
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<SubscriptionData[]>([]);
  const [diamondPass, setDiamondPass] = useState<DiamondPass | null>(null);
  const [profileDataCache, setProfileDataCache] = useState<UserProfileData | null>(null);
  const profileFetchPromiseRef = useRef<{
    profileId: string;
    promise: Promise<{ userData: UserProfileData | null, error: PostgrestError | unknown | null }>;
  } | null>(null);
  const activeProfileIdRef = useRef<string | null>(null);
  const supabase = getBrowserClient();

  useEffect(() => {
    const getInitialSession = async () => {
      try {
        const userResponse = await supabase.auth.getUser();
        if (userResponse.error) {
          if (!isExpectedMissingSession(userResponse.error)) {
            console.error('Error getting session:', userResponse.error);
          }
        } else if (userResponse.data.user) {
          activeProfileIdRef.current = userResponse.data.user.id;
          setUser(userResponse.data.user);

          const attribution = getAttributionForEvent();
          phIdentify(userResponse.data.user.id, {
            email: userResponse.data.user.email,
            ...attribution,
          });
          Sentry.setUser({ id: userResponse.data.user.id, email: userResponse.data.user.email });

          const [subs] = await Promise.all([
            fetchSubscriptions(userResponse.data.user.id),
            ensureProfileData(userResponse.data.user.id),
          ]);
          seedPublicNavState(userResponse.data.user, subs);
          registerTrialMetadata(userResponse.data.user.id, subs);
        } else {
          invalidatePublicNavState();
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error);
      } finally {
        setIsLoading(false);
      }
    };

    let authSubscription: Subscription | null = null;
    getInitialSession().then(() => {
      const authState = supabase.auth.onAuthStateChange(
        (event, session) => {
          setTimeout(async () => {
            if (event === "INITIAL_SESSION" && session?.user?.id === activeProfileIdRef.current) return;
            if (session?.user) {
              activeProfileIdRef.current = session.user.id;
              setUser(session.user);
              const [subs] = await Promise.all([
                fetchSubscriptions(session.user.id),
                ensureProfileData(session.user.id),
              ]);
              seedPublicNavState(session.user, subs);

              const attribution = getAttributionForEvent();
              phIdentify(session.user.id, {
                email: session.user.email,
                ...attribution,
              });
              Sentry.setUser({ id: session.user.id, email: session.user.email });

              registerTrialMetadata(session.user.id, subs);
            } else {
              activeProfileIdRef.current = null;
              setUser(null);
              setSubscriptions([]);
              setDiamondPass(null);
              setProfileDataCache(null);
              profileFetchPromiseRef.current = null;
              invalidatePublicNavState();
              Sentry.setUser(null);

              if (event === "SIGNED_OUT") {
                phReset();
              }
            }
            setIsLoading(false);
          });
        }
      );
      authSubscription = authState.data.subscription;
    });

    return () => {
      authSubscription?.unsubscribe();
    };
  }, [supabase.auth]);

  const fetchDiamondPass = async (userId: string): Promise<void> => {
    const { data, error } = await supabase
      .from('diamond_passes')
      .select('ends_at')
      .eq('profile_id', userId)
      .maybeSingle();
    setDiamondPass(error ? null : data);
  };

  const fetchSubscriptions = async (userId: string): Promise<SubscriptionData[]> => {
    fetchDiamondPass(userId).catch(() => setDiamondPass(null));
    try {
      const { data, error } = await supabase
        .from('customer_subscriptions')
        .select()
        .eq('profile_id', userId);

      if (error || data === null || data.length === 0) {
        console.error('Error fetching subscription:', error);
        setSubscriptions([]);
        return [];
      } else {
        const subs = data as SubscriptionData[];
        setSubscriptions(subs);
        return subs;
      }
    } catch (error) {
      console.error('Error in fetchSubscriptionInfo:', error);
      setSubscriptions([]);
      return [];
    }
  };

  const ensureProfileData = async (profileId: string) => {
    if (!profileId) {
      setProfileDataCache(null);
      return { userData: null, error: new Error('Missing profile id') };
    }

    if (profileDataCache && user?.id === profileId) {
      return { userData: profileDataCache, error: null };
    }

    if (profileFetchPromiseRef.current?.profileId === profileId) {
      return profileFetchPromiseRef.current.promise;
    }

    const fetchPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select()
          .eq('id', profileId)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          setProfileDataCache(null);
          return { userData: null, error };
        }

        const userData: UserProfileData | null = data
          ? {
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            postal_code: data.postal_code || '',
            rewardful_via: data.rewardful_via || '',
          }
          : null;

        if (activeProfileIdRef.current === profileId) {
          setProfileDataCache(userData);
        }
        return { userData, error: null };
      } catch (error) {
        console.error('Unexpected error fetching profile:', error);
        if (activeProfileIdRef.current === profileId) {
          setProfileDataCache(null);
        }
        return { userData: null, error };
      } finally {
        if (profileFetchPromiseRef.current?.profileId === profileId) {
          profileFetchPromiseRef.current = null;
        }
      }
    })();

    profileFetchPromiseRef.current = {
      profileId,
      promise: fetchPromise,
    };
    return fetchPromise;
  };

  const sendOTPCode = async (email: string): Promise<{ error: any | null }> => {
    try {
      const cleanEmail = email.trim().toLowerCase();
      const { error } = await supabase.auth.signInWithOtp({
        email: cleanEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: undefined,
        },
      });

      if (error) {
        console.error('Send OTP error:', error);
        const errorCategory = categorizeOtpError(error.message);

        const isNotFound = errorCategory === "user_not_found";
        if (isNotFound) {
          const normalized = normalizeEmailForLookup(cleanEmail);
          if (normalized !== cleanEmail) {
            console.log("[email-heal] Attempting sign-in heal for", cleanEmail);
            try {
              const result = await healGmailEmail(cleanEmail);
              if (result.healed) {
                console.log("[email-heal] Healed successfully, retrying OTP");
                const retry = await supabase.auth.signInWithOtp({
                  email: cleanEmail,
                  options: {
                    shouldCreateUser: false,
                    emailRedirectTo: undefined,
                  },
                });
                if (!retry.error) {
                  phCapture('otp_sent', { method: 'email', trigger: 'signin', healed: true });
                }
                return { error: retry.error || null };
              }
            } catch (healError) {
              console.error('[email-heal] Healing attempt failed:', healError);
            }
          }
        }

        phCapture('send_otp_failed', {
          error: error.message,
          error_category: errorCategory,
          trigger: 'signin',
        });

        return { error };
      }

      phCapture('otp_sent', { method: 'email', trigger: 'signin' });
      return { error: null };
    } catch (error) {
      console.error('Unexpected error sending OTP:', error);
      return { error };
    }
  };

  const verifyOTPAndSignIn = async (
    email: string,
    otp: string
  ): Promise<{ error: any | null }> => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: otp,
        type: 'email',
      });

      if (error) {
        console.error('Verify OTP error:', error);
        const errorCategory = categorizeOtpError(error.message);

        phCapture('verify_otp_failed', {
          error: error.message,
          error_category: errorCategory,
        });

        return { error };
      }

      if (data.user) {
        setUser(data.user);
        activeProfileIdRef.current = data.user.id;

        await Promise.all([
          fetchSubscriptions(data.user.id),
          ensureProfileData(data.user.id),
        ]);

        phCapture('user_signed_in', { method: 'otp' });
        return { error: null };
      }

      return { error: new Error('No user data returned') };
    } catch (error) {
      console.error('Unexpected error verifying OTP:', error);
      phCapture('verify_otp_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        error_category: 'exception',
      });
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const resendOTPCode = async (email: string): Promise<{ error: any | null }> => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: {
          shouldCreateUser: false,
          emailRedirectTo: undefined,
        },
      });

      if (error) {
        console.error('Resend OTP error:', error);
        const errorCategory = categorizeOtpError(error.message);

        phCapture('resend_otp_failed', {
          error: error.message,
          error_category: errorCategory,
        });
        return { error };
      }

      phCapture('otp_resent', { method: 'email', trigger: 'signin' });
      return { error: null };
    } catch (error) {
      console.error('Unexpected error resending OTP:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string): Promise<{ error: any | null }> => {
    try {
      setIsLoading(true);
      const { data: {
        user: signedInUser
      }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      setUser(signedInUser);
      activeProfileIdRef.current = signedInUser?.id ?? null;
      if (signedInUser?.id) {
        await Promise.all([
          fetchSubscriptions(signedInUser.id),
          ensureProfileData(signedInUser.id),
        ]);
      }

      if (error) {
        console.error('Sign in error:', error);
        let errorCategory = 'unknown';
        if (error.message?.includes('Invalid login credentials')) {
          errorCategory = 'invalid_credentials';
        } else if (error.message?.includes('Email not confirmed')) {
          errorCategory = 'unconfirmed_email';
        } else if (error.message?.includes('User not found')) {
          errorCategory = 'user_not_found';
        }
        phCapture('sign_in_failed', {
          error: error.message,
          error_category: errorCategory,
        });
      } else if (signedInUser) {
        phCapture('user_signed_in', { method: 'password' });
      }

      return { error };
    } catch (error) {
      console.error('Unexpected error during sign in:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    userData: UserProfileData,
    stripeCustomerId?: string
  ): Promise<{ error: any | null }> => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName: userData.first_name,
            lastName: userData.last_name,
            address: userData.address,
            city: userData.city,
            state: userData.state,
            postalCode: userData.postal_code,
          }
        }
      });

      if (error) {
        console.error('Sign up error:', error);
        phCapture('sign_up_failed', { error: error.message });
      } else if (data.user) {
        try {
          await supabase
            .from('profiles')
            .upsert({
              id: data.user.id,
              first_name: userData.first_name,
              last_name: userData.last_name,
              address: userData.address,
              city: userData.city,
              state: userData.state,
              postal_code: userData.postal_code,
              stripe_customer_id: stripeCustomerId,
              updated_at: new Date().toISOString(),
            });
        } catch (profileError) {
          console.error('Error creating/updating profile:', profileError);
        }

        const aliasKey = `ph_alias_done:${data.user.id}`;
        try {
          const alreadyAliased =
            typeof window !== "undefined" &&
            window.sessionStorage &&
            window.sessionStorage.getItem(aliasKey) === "true";

          if (!alreadyAliased) {
            phAlias(data.user.id);
            try {
              window.sessionStorage?.setItem(aliasKey, "true");
            } catch {
            }
          }
        } catch {
          phAlias(data.user.id);
        }
      }

      return { error };
    } catch (error) {
      console.error('Unexpected error during sign up:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setIsLoading(true);

    phReset();

    const cleanupAndRedirect = () => {
      setUser(null);
      setSubscriptions([]);
      setDiamondPass(null);
      setProfileDataCache(null);
      profileFetchPromiseRef.current = null;
      activeProfileIdRef.current = null;
      invalidatePublicNavState();
      window.location.href = '/signin';
    };

    const nukeSupabaseStorage = () => {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        if (name.startsWith('sb-')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      }

      try {
        const storage = window.localStorage;
        const keysToRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (key && key.startsWith('sb-')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => storage.removeItem(key));
      } catch {
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      await Promise.race([
        new Promise<void>((resolve) => {
          timeoutId = setTimeout(() => {
            nukeSupabaseStorage();
            cleanupAndRedirect();
            resolve();
          }, 2000);
        }),
        (async () => {
          await revokeThisDevice().catch(() => {});
          await supabase.auth.signOut();
          nukeSupabaseStorage();
          cleanupAndRedirect();
        })(),
      ]);
    } catch (error) {
      console.error('Error during sign out process:', error);
      nukeSupabaseStorage();
      cleanupAndRedirect();
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
    }
  };

  const refreshSubscription = async (): Promise<void> => {
    if (user?.id) {
      await fetchSubscriptions(user.id);
      await ensureProfileData(user.id);
    }
  };

  const updateProfile = async (userData: UserProfileData): Promise<{ userData: UserProfileData | null, error: PostgrestError | unknown | null }> => {
    try {
      if (!user?.id) {
        return { userData: null, error: new Error('No authenticated user') };
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(userData)
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Error updating profile:', error);
      }

      if (data && data[0]) {
        const updatedProfile: UserProfileData = {
          first_name: data[0].first_name || '',
          last_name: data[0].last_name || '',
          address: data[0].address || '',
          city: data[0].city || '',
          state: data[0].state || '',
          postal_code: data[0].postal_code || '',
          rewardful_via: data[0].rewardful_via || '',
        };
        setProfileDataCache(updatedProfile);
        return {
          userData: updatedProfile,
          error,
        };
      }

      return {
        userData: profileDataCache,
        error
      };
    } catch (error) {
      console.error('Unexpected error updating profile:', error);
      return { userData: null, error };
    }
  };

  const getProfileData = async (): Promise<{ userData: UserProfileData | null, error: PostgrestError | unknown | null }> => {
    try {
      if (!user?.id) {
        return { userData: null, error: new Error('No authenticated user') };
      }

      activeProfileIdRef.current = user.id;

      const cached = await ensureProfileData(user.id);
      return cached;
    } catch (error) {
      console.error('Unexpected error fetching profile:', error);
      return { userData: null, error };
    }
  };

  const isBypassAuth = isLocalBypassAuthEnabled();
  const isAuthenticated = user !== null && user !== undefined;
  const hasVerifiedEmailVal = Boolean(user?.email_confirmed_at);
  const hasSubHistory = subscriptions.some((s) => s.status != null && hasSubscriptionHistory(s.status));
  const hasOddsAccess = isBypassAuth || hasVerifiedEmailVal || hasSubHistory;

  const value: AuthContextValue = {
    user,
    isLoading,
    subscriptions: subscriptions,
    diamondPass,
    isAuthenticated,
    hasVerifiedEmail: hasVerifiedEmailVal,
    hasSubscriptionHistory: hasSubHistory,
    hasOddsScreenAccess: hasOddsAccess,
    sendOTPCode,
    verifyOTPAndSignIn,
    resendOTPCode,
    signIn,
    signUp,
    signOut,
    refreshSubscription,
    updateProfile,
    getProfileData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext) ?? unauthenticatedAuthContextValue;
}


export function useHasAuthProvider(): boolean {
  return useContext(AuthContext) !== undefined;
}
